import {
  formatDayMonth,
  type Lang,
  type Me,
  type PaymentProviderCode,
  type StoriesResponse,
} from '@fonus/shared';
import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { t as translate } from '@fonus/shared/i18n';
import { authenticate, onAuthenticated, onFatalAuth } from './api/client.ts';
import { keys, useMe, useStories, useUpdateMe } from './api/queries.ts';
import { configureEngine, playStory, prefetchStory, toggle, usePlayer } from './audio/engine.ts';
import { BOT_USERNAME, SUPPORT_URL } from './env.ts';
import { useLang, useT } from './i18n/useT.ts';
import { usePlayItem } from './lib/actions.ts';
import { applyPlayResponse } from './lib/catalog.ts';
import { Home, HomeSkeleton } from './screens/Home.tsx';
import { StateScreen } from './screens/StateScreen.tsx';
import { topLayer, useUi } from './state/ui.ts';
import {
  closeApp,
  currentTheme,
  onThemeChange,
  openTelegramLink,
  ready,
  setBackButton,
  shareLink,
} from './telegram/tg.ts';
import { MenuSheet } from './ui/Sheet.tsx';
import { MiniPlayer, ToastView } from './ui/story.tsx';

// Lazy chunks (spec §8: code splitting for player and paywall; keeps the first screen ≤ 120 KB).
const loadPlayer = () => import('./sheets/PlayerV2.tsx');
const loadPaywall = () => import('./sheets/PaywallV2.tsx');
const loadProfile = () => import('./sheets/ProfileSheet.tsx');
const PlayerV2 = lazy(() => loadPlayer().then((m) => ({ default: m.PlayerV2 })));
const SleepPicker = lazy(() => loadPlayer().then((m) => ({ default: m.SleepPicker })));
const PaywallV2 = lazy(() => loadPaywall().then((m) => ({ default: m.PaywallV2 })));
const PaymentV2 = lazy(() => loadPaywall().then((m) => ({ default: m.PaymentV2 })));
const ProfileSheet = lazy(() => loadProfile().then((m) => ({ default: m.ProfileSheet })));
const LanguageSheet = lazy(() => loadProfile().then((m) => ({ default: m.LanguageSheet })));
const WelcomeSheet = lazy(() => loadProfile().then((m) => ({ default: m.WelcomeSheet })));

const EXPIRED_SEEN_KEY = 'fonus-ertak-expired-seen';
const WELCOME_SEEN_KEY = 'fonus-ertak-welcome-seen';

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode
  }
}

export function App() {
  const qc = useQueryClient();
  const t = useT();
  const lang = useLang();
  const ui = useUi();
  const [authed, setAuthed] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const me = useMe(authed);
  const stories = useStories(lang, authed);
  const updateMe = useUpdateMe();
  const hasStory = usePlayer((s) => s.story !== null);
  const top = topLayer(ui);
  const playItem = usePlayItem(lang);
  const [dark, setDark] = useState(() => currentTheme() === 'dark');
  /** "Story language" in the player menu: replay the current story once the new list arrives. */
  const replay = useRef<{ id: string; lang: Lang } | null>(null);
  const [storyLangPick, setStoryLangPick] = useState(false);

  useEffect(
    () =>
      onThemeChange((th) => {
        setDark(th === 'dark');
      }),
    [],
  );

  // ── Auth bootstrap ──
  useEffect(() => {
    const offAuth = onAuthenticated((auth) => {
      qc.setQueryData<Me>(keys.me, auth.me);
      if (auth.isNew && storageGet(WELCOME_SEEN_KEY) === null)
        useUi.getState().open({ kind: 'welcome' });
      // Bot buttons "Renew" / "Get Premium" open the Mini App with ?screen=paywall (once).
      const params = new URLSearchParams(window.location.search);
      if (params.get('screen') === 'paywall') {
        params.delete('screen');
        const query = params.toString();
        window.history.replaceState(
          null,
          '',
          window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
        );
        useUi.getState().open({ kind: 'paywall' });
      }
    });
    const offFatal = onFatalAuth(() => {
      useUi.getState().setFatal('reopen');
    });
    authenticate()
      .then(() => {
        setAuthed(true);
      })
      .catch(() => {
        // Network down: cached data still renders; queries retry once online.
        setAuthed(true);
      });
    return () => {
      offAuth();
      offFatal();
    };
  }, [qc]);

  // ── Engine wiring ──
  useEffect(() => {
    configureEngine({
      onPaywall: () => {
        useUi.getState().closeKind('player');
        useUi.getState().open({ kind: 'paywall' });
        void qc.invalidateQueries({ queryKey: ['stories'] });
      },
      onAccessChanged: (res, id) => {
        applyPlayResponse(qc, res, id);
      },
      onCompleted: () => {
        void qc.invalidateQueries({ queryKey: ['stories'] });
      },
      onError: (key) => {
        useUi.getState().showToast(t(key));
      },
      qualityPref: () => qc.getQueryData<Me>(keys.me)?.audioQuality ?? 'AUTO',
      onSlowNetwork: () => {
        useUi.getState().showToast(t('toast.slowNetwork'));
      },
    });
  }, [qc, t]);

  // ── Telegram BackButton closes the top layer ──
  useEffect(() => {
    setBackButton(
      top
        ? () => {
            useUi.getState().close();
          }
        : null,
    );
  }, [top]);

  // ── Online / offline ──
  useEffect(() => {
    const on = () => {
      setOnline(true);
      void qc.invalidateQueries();
    };
    const off = () => {
      setOnline(false);
    };
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [qc]);

  const data = stories.data;
  const tonightId = data?.items.find((i) => i.isTonight)?.id;

  useEffect(() => {
    const want = replay.current;
    if (!want || want.lang !== lang || !data || stories.isPlaceholderData) return;
    replay.current = null;
    const item = data.items.find((i) => i.id === want.id);
    if (item) playItem(item, { openPlayer: false });
  }, [lang, data, stories.isPlaceholderData, playItem]);

  // ── First paint ready + prefetch tonight's audio + warm lazy chunks ──
  useEffect(() => {
    if (!data) return;
    ready();
    if (tonightId) void prefetchStory(tonightId, lang);
    const warm = () => {
      void loadPlayer();
      void loadPaywall();
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(warm);
    else setTimeout(warm, 1200);
  }, [data, tonightId, lang]);

  // ── "Subscription ended" once per ended period ──
  const endedAt = me.data?.premiumUntil ? null : (me.data?.premiumEndedAt ?? null);
  useEffect(() => {
    if (endedAt && storageGet(EXPIRED_SEEN_KEY) !== endedAt) {
      storageSet(EXPIRED_SEEN_KEY, endedAt);
      useUi.getState().open({ kind: 'expired' });
    }
  }, [endedAt]);

  // ── Offline with cache → toast ──
  useEffect(() => {
    if (!online && data) useUi.getState().showToast(t('toast.offline'));
  }, [online, data, t]);

  const switchLang = (l: Lang) => {
    updateMe.mutate({ lang: l });
    // Confirm in the language the user just picked.
    useUi.getState().showToast(translate(l, 'toast.langChanged'), 'success');
  };

  const name = me.data?.firstName ?? '';
  const expired =
    Boolean(data?.premiumEndedAt ?? me.data?.premiumEndedAt) &&
    !(data?.premiumUntil ?? me.data?.premiumUntil);

  let screen;
  if (ui.fatal === 'reopen') {
    screen = (
      <StateScreen
        kind="reopen"
        title={t('state.reopenTitle')}
        body={t('state.reopenBody')}
        cta={t('state.reopenButton')}
        onCta={closeApp}
      />
    );
  } else if (data && data.items.length === 0) {
    screen = (
      <StateScreen
        kind="empty"
        header={{ name, t }}
        title={t('state.emptyTitle')}
        body={lang === 'uz' ? t('state.emptyUzBody') : t('state.emptyBody')}
        cta={lang === 'uz' ? t('state.reload') : t('state.emptyCta')}
        onCta={() => {
          if (lang === 'uz') void stories.refetch();
          else switchLang('uz');
        }}
      />
    );
  } else if (data) {
    screen = (
      <Home me={{ firstName: name }} data={data} lang={lang} onLangChange={switchLang} t={t} />
    );
  } else if (!online) {
    screen = (
      <StateScreen
        kind="offline"
        header={{ name, t }}
        title={t('state.offlineTitle')}
        body={t('state.offlineBody')}
        cta={t('state.retry')}
        onCta={() => void stories.refetch()}
      />
    );
  } else if (stories.isError) {
    screen = (
      <StateScreen
        kind="error"
        header={{ name, t }}
        title={t('state.errorTitle')}
        body={t('state.errorBody')}
        cta={t('state.reload')}
        onCta={() => void stories.refetch()}
      />
    );
  } else {
    screen = <HomeSkeleton />;
  }

  const close = ui.close;
  const paywall = ui.layers.find((l) => l.kind === 'paywall');
  const isOpen = (kind: string) => ui.layers.some((l) => l.kind === kind);

  return (
    <>
      {screen}

      {hasStory && ui.fatal === null && (
        <ConnectedMiniPlayer
          t={t}
          onOpen={() => {
            ui.open({ kind: 'player' });
          }}
        />
      )}

      <Suspense fallback={null}>
        {isOpen('expired') && me.data?.premiumEndedAt && (
          <StateScreen
            overlay
            kind="expired"
            header={{ name, t }}
            title={t('state.expiredTitle')}
            body={t('state.expiredBody', {
              date: formatDayMonth(new Date(me.data.premiumEndedAt), lang),
            })}
            cta={t('state.renew')}
            onCta={() => {
              ui.replaceTop({ kind: 'paywall' });
            }}
            secondary={{ label: t('state.later'), onClick: close }}
          />
        )}
        {isOpen('player') && (
          <PlayerV2
            t={t}
            lang={lang}
            dark={dark}
            name={name}
            reminderTime={me.data?.reminderTime ?? '20:30'}
            items={data?.items ?? []}
            onClose={() => {
              ui.closeKind('player');
            }}
            onPlay={(item) => {
              playItem(item, { openPlayer: false });
            }}
          />
        )}
        {isOpen('sleep') && (
          <SleepPicker
            t={t}
            onClose={() => {
              ui.closeKind('sleep');
            }}
          />
        )}
        {isOpen('more') && (
          <MenuSheet
            label={t('player.more')}
            cancelLabel={t('menu.cancel')}
            onClose={() => {
              ui.closeKind('more');
            }}
            items={[
              ...(BOT_USERNAME
                ? [
                    {
                      label: t('menu.share'),
                      onSelect: () => {
                        ui.closeKind('more');
                        const ref = me.data ? `?start=ref_${me.data.id}` : '';
                        shareLink(`https://t.me/${BOT_USERNAME}${ref}`, t('menu.shareText'));
                      },
                    },
                  ]
                : []),
              {
                label: t('menu.storyLang'),
                value: lang.toUpperCase(),
                onSelect: () => {
                  setStoryLangPick(true);
                  ui.replaceTop({ kind: 'language' });
                },
              },
              {
                label: t('menu.textSize'),
                value: ui.largeText ? t('menu.textSizeLarge') : t('menu.textSizeNormal'),
                onSelect: () => {
                  ui.toggleLargeText();
                },
              },
              {
                label: t('menu.report'),
                onSelect: () => {
                  ui.closeKind('more');
                  openTelegramLink(SUPPORT_URL);
                },
              },
            ]}
          />
        )}
        {isOpen('profile') && me.data && (
          <ProfileSheet
            me={me.data}
            lang={lang}
            t={t}
            onClose={() => {
              ui.closeKind('profile');
            }}
            onOpenLanguage={() => {
              ui.open({ kind: 'language' });
            }}
            onOpenPaywall={() => {
              ui.open({ kind: 'paywall' });
            }}
          />
        )}
        {isOpen('language') && (
          <LanguageSheet
            lang={lang}
            t={t}
            onSelect={(l) => {
              const current = usePlayer.getState().story;
              if (storyLangPick && current && l !== lang)
                replay.current = { id: current.id, lang: l };
              switchLang(l);
            }}
            onClose={() => {
              setStoryLangPick(false);
              ui.closeKind('language');
            }}
          />
        )}
        {paywall?.kind === 'paywall' && (
          <PaywallV2
            t={t}
            lang={lang}
            me={me.data}
            story={paywall.story}
            expired={expired}
            onClose={() => {
              ui.closeKind('paywall');
            }}
          />
        )}
        {top?.kind === 'payment' && (
          <PaymentV2
            key={top.paymentId}
            t={t}
            lang={lang}
            me={me.data}
            paymentId={top.paymentId}
            orderNo={top.orderNo}
            payUrl={top.payUrl}
            provider={top.provider as PaymentProviderCode}
            planCode={top.planCode}
            amountUzs={top.amountUzs}
            onListen={() => {
              // Premium is active now: play the story that opened the paywall (else tonight's)
              // directly — the cached list may still mark it locked until the refetch lands.
              const list = qc.getQueryData<StoriesResponse>(keys.stories(lang))?.items ?? [];
              const item =
                list.find((i) => i.id === top.story?.id) ?? list.find((i) => i.isTonight);
              ui.closeAll();
              if (!item) return;
              ui.open({ kind: 'player' });
              void playStory(
                {
                  id: item.id,
                  title: item.title,
                  summary: item.summary,
                  hue: item.hue,
                  durationSec: item.durationSec,
                  isTonight: item.isTonight,
                },
                lang,
              );
            }}
            onHome={() => {
              ui.closeAll();
            }}
            onRetry={() => {
              ui.replaceTop({ kind: 'paywall', story: top.story });
            }}
            onClose={() => {
              ui.replaceTop({ kind: 'paywall', story: top.story });
            }}
          />
        )}
        {isOpen('welcome') && (
          <WelcomeSheet
            freeLimit={me.data?.freeLimit ?? 3}
            t={t}
            onClose={() => {
              storageSet(WELCOME_SEEN_KEY, '1');
              ui.closeKind('welcome');
            }}
          />
        )}
      </Suspense>

      {ui.toast && <ToastView key={ui.toast.id} text={ui.toast.text} tone={ui.toast.tone} />}
    </>
  );
}

/** Mini player subscribes to position itself, so the rest of the app doesn't re-render 4×/s. */
function ConnectedMiniPlayer({ t, onOpen }: { t: ReturnType<typeof useT>; onOpen: () => void }) {
  const story = usePlayer((s) => s.story);
  const status = usePlayer((s) => s.status);
  const position = usePlayer((s) => s.position);
  const duration = usePlayer((s) => s.duration);
  if (!story) return null;
  return (
    <MiniPlayer
      title={story.title}
      hue={story.hue}
      position={position}
      duration={duration || (story.durationSec ?? 0)}
      playing={status === 'playing' || status === 'buffering'}
      t={t}
      onOpen={onOpen}
      onToggle={toggle}
    />
  );
}
